/* eslint-disable react/prop-types */
import * as React from 'react';

import { useBoolean, useMemoizedFn } from 'ahooks';
import { Button, Col, Form, Input, Popconfirm, Row, Space } from 'antd';
import dayjs from 'dayjs';

import {
  AntdDatePicker,
  AntdUpload,
  AntdUploadDragger,
  DeleteOutlinedIcon,
  DocViewer,
  IntegerInputNumber,
  PrimitiveList,
  PrimitiveListItem,
  PrimitiveListItemMeta,
  QuestionCircleFilledIcon,
  UploadOutlinedIcon,
} from 'components';
import { useCatalogAction, useCatalogValue } from 'context';
import type { CatalogFile, FileInfo } from 'types';
import { FILE, generateDownloadFile, inputRule } from 'utils';

const {
  DOCUMENT: {
    SIZE: { WIDTH: DOCUMENT_SIZE_WIDTH, HEIGHT: DOCUMENT_SIZE_HEIGHT },
  },
} = FILE;

type FieldType = Record<string, string>;

interface CatalogFileInfoProps extends CatalogFile {
  maxIndex: number;
  handleDeleteFile: () => void;
}

const CatalogFileInfo = React.memo<CatalogFileInfoProps>(
  ({
    id,
    title,
    publishTime,
    pageCount,
    banner: { id: bannerId, name: bannerName } = {},
    file: { id: fileId, name: fileName },
    maxIndex,
    handleDeleteFile,
  }) => {
    const { catalogs = [], ownerType } = useCatalogValue();
    const action = useCatalogAction();

    if (!ownerType) return <React.Fragment />;

    return (
      <PrimitiveListItem className='my-5'>
        <PrimitiveListItemMeta
          title={
            <Row>
              <Col span={24} lg={8} className='lg:pe-3'>
                <div className='h-full max-lg:mb-[--ant-form-item-margin-bottom]'>
                  <DocViewer
                    className='h-64'
                    documents={[
                      {
                        uri: generateDownloadFile(
                          ownerType,
                          fileId,
                          DOCUMENT_SIZE_WIDTH,
                          DOCUMENT_SIZE_HEIGHT,
                        ),
                        fileName,
                      },
                    ]}
                  />
                </div>
              </Col>
              <Col span={24} lg={16} className='lg:ps-3'>
                <Row>
                  <Col span={24}>
                    <Space.Compact className='w-full overflow-auto'>
                      <Form.Item<FieldType>
                        name={`file-index-${id}`}
                        className='shrink-0 basis-1/12 [&_*]:h-full'
                      >
                        <IntegerInputNumber
                          placeholder='رتبه'
                          className='border-e-0'
                          max={maxIndex}
                        />
                      </Form.Item>
                      <Form.Item<FieldType>
                        label='برنما'
                        name={`file-banner-id-${id}`}
                        className='basis-11/12 [&_.ant-form-item-label]:sr-only'
                        valuePropName='file'
                        rules={!bannerId ? [inputRule.required] : undefined}
                      >
                        <AntdUpload
                          ownerType={ownerType}
                          fileList={
                            bannerId
                              ? [
                                  {
                                    uid: bannerId,
                                    name: bannerName ?? 'برنما مستند',
                                    status: 'done',
                                    url: generateDownloadFile(
                                      ownerType,
                                      bannerId,
                                    ),
                                  },
                                ]
                              : undefined
                          }
                          onChange={({ file: { status, response } }) => {
                            const updatedCatalogs = catalogs.map(catalog => {
                              if (id === catalog.id) {
                                let banner: FileInfo | undefined = undefined;

                                if (status === 'done' && response) {
                                  banner = response;
                                }

                                return {
                                  ...catalog,
                                  banner,
                                };
                              }

                              return catalog;
                            });

                            action({
                              type: 'UPDATE_CATALOG_FILES',
                              payload: {
                                catalogs: updatedCatalogs,
                              },
                            });
                          }}
                        >
                          <Button icon={<UploadOutlinedIcon />}>
                            بارگذاری برنما (Banner)
                          </Button>
                        </AntdUpload>
                      </Form.Item>
                    </Space.Compact>
                  </Col>
                  <Col span={24}>
                    <Form.Item<FieldType>
                      label='عنوان'
                      name={`file-title-${id}`}
                      rules={[inputRule.required, inputRule.string]}
                      initialValue={title}
                    >
                      <Input placeholder='عنوان مستند را وارد کنید' />
                    </Form.Item>
                  </Col>
                  <Col span={24} lg={12} className='lg:pe-1.5'>
                    <Form.Item<FieldType>
                      label='تاریخ انتشار'
                      name={`file-publish-time-${id}`}
                      rules={[inputRule.required]}
                      initialValue={
                        publishTime ? [dayjs(publishTime)] : undefined
                      }
                      className='lg:mb-0'
                    >
                      <AntdDatePicker
                        placeholder='تاریخ انتشار مستند را انتخاب کنید'
                        onDateChange={([date]) => {
                          if (!date) return;

                          const updatedCatalogs = catalogs.map(catalog => {
                            if (id === catalog.id) {
                              return {
                                ...catalog,
                                publishTime: date,
                              };
                            }

                            return catalog;
                          });

                          action({
                            type: 'UPDATE_CATALOG_FILES',
                            payload: {
                              catalogs: updatedCatalogs,
                            },
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} lg={12} className='lg:ps-1.5'>
                    <Form.Item<FieldType>
                      label=' '
                      className='mb-0 max-lg:-mt-4'
                    >
                      <Popconfirm
                        title='تأیید حذف مستند'
                        description='با حذف این مستند، تمام اطلاعات مرتبط نیز پاک می‌شوند.'
                        okButtonProps={{
                          className: 'bg-[--ant-color-error]',
                        }}
                        icon={
                          <QuestionCircleFilledIcon className='text-[--ant-color-error]' />
                        }
                        onConfirm={handleDeleteFile}
                      >
                        <Button
                          danger
                          type='primary'
                          className='w-full'
                          icon={<DeleteOutlinedIcon />}
                        >
                          حذف مستند
                        </Button>
                      </Popconfirm>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          }
        />
      </PrimitiveListItem>
    );
  },
);
CatalogFileInfo.displayName = 'CatalogFileInfo';

const CatalogFilesInfo = () => {
  const [catalogsLoading, { set: setCatalogsLoading }] = useBoolean();
  const { catalogs = [], ownerType } = useCatalogValue();
  const action = useCatalogAction();

  const handleDeleteCatalog = useMemoizedFn((catalogId: string) => {
    const updatedCatalogs = catalogs.filter(({ id }) => id !== catalogId);

    action({
      type: 'UPDATE_CATALOG_FILES',
      payload: {
        catalogs: updatedCatalogs,
      },
    });
  });

  if (!ownerType) return <React.Fragment />;

  const totalCatalogs = catalogs.length;

  return (
    <Row>
      <Col span={24}>
        <Form.Item>
          <AntdUploadDragger
            withPictures={false}
            withVideos={false}
            ownerType={ownerType}
            disabled={catalogsLoading}
            onChange={({ file: { status, response } }) => {
              if (status === 'uploading') {
                setCatalogsLoading(true);
              }
              if (status === 'done' && response) {
                const { id, name } = response as FileInfo;
                const publishTime = new Date(Date.now()).toISOString();

                action({
                  type: 'UPDATE_CATALOG_FILES',
                  payload: {
                    catalogs: [
                      ...catalogs,
                      {
                        id,
                        title: name,
                        publishTime,
                        file: response,
                      },
                    ],
                  },
                });
              }
              if (status !== 'uploading') {
                setCatalogsLoading(false);
              }
            }}
          />
        </Form.Item>
        <PrimitiveList
          loading={{
            spinning: catalogsLoading,
          }}
          dataSource={catalogs}
          renderItem={item => {
            const { id } = item;
            return (
              <CatalogFileInfo
                maxIndex={totalCatalogs}
                handleDeleteFile={() => {
                  handleDeleteCatalog(id);
                }}
                {...item}
              />
            );
          }}
        />
      </Col>
    </Row>
  );
};

export { CatalogFileInfo, CatalogFilesInfo };
